import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Heart, Sparkles, Sun, Moon, Star, Flower2, X, Loader2, Check } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = window.location.origin;

// Gentle affirmations for different focus areas
const AFFIRMATIONS = {
  career: [
    "Your work matters, even when no one is watching.",
    "Every step forward, no matter how small, is progress.",
    "You are capable of more than you know.",
    "Trust the timing of your journey.",
    "Your gifts are needed in this world."
  ],
  relationships: [
    "You deserve love that feels like home.",
    "Healthy boundaries are an act of self-love.",
    "Your heart is healing, even when it doesn't feel like it.",
    "The right connections will find you.",
    "You are worthy of deep, genuine connection."
  ],
  faith: [
    "You are held, even when you can't feel it.",
    "Your faith is a seed that grows in silence.",
    "Rest in knowing you don't have to have all the answers.",
    "Grace meets you exactly where you are.",
    "Your spirit knows the way, trust it."
  ],
  rest: [
    "Rest is not a reward, it's a requirement.",
    "You are allowed to pause and breathe.",
    "Your worth is not measured by your productivity.",
    "Stillness is where healing happens.",
    "Give yourself permission to just be."
  ],
  visibility: [
    "Your voice deserves to be heard.",
    "You don't have to shrink to make others comfortable.",
    "Your story has the power to inspire.",
    "Step into the light — you were made for it.",
    "Being seen is an act of courage."
  ],
  general: [
    "You are exactly where you need to be.",
    "Today, you are enough. Tomorrow, you will be enough too.",
    "Small wins are still wins.",
    "Be gentle with yourself — you're doing better than you think.",
    "Your journey is unfolding perfectly."
  ]
};

// Progress acknowledgments (non-points-based)
const PROGRESS_MESSAGES = {
  first_post: "You shared your voice with the community. That took courage.",
  first_journal: "You started reflecting on your journey. That's powerful.",
  week_streak: "You've shown up for yourself all week. That's beautiful.",
  community_connection: "You've been building genuine connections. Keep nurturing them.",
  focus_path_started: "You've set an intention for your growth. That's the first step.",
  ten_journals: "Ten entries of self-reflection. You're learning yourself.",
  first_circle: "You've created a space for connection. That's leadership.",
  helped_another: "You offered support to a sister. That ripples outward."
};

// Quiet Recognition Card Component
export const QuietRecognitionCard = ({ type, focusArea = 'general', onDismiss }) => {
  const [visible, setVisible] = useState(true);
  const affirmations = AFFIRMATIONS[focusArea] || AFFIRMATIONS.general;
  const affirmation = affirmations[Math.floor(Math.random() * affirmations.length)];
  
  const icons = [Heart, Sparkles, Sun, Star, Flower2];
  const Icon = icons[Math.floor(Math.random() * icons.length)];

  if (!visible) return null;

  const handleDismiss = () => {
    setVisible(false);
    if (onDismiss) onDismiss();
  };

  return (
    <Card className="card-base border-l-4 border-l-purple-400 bg-gradient-to-r from-purple-50 to-pink-50/30 relative overflow-hidden">
      <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-pink-100/50 to-transparent rounded-bl-full" />
      <CardContent className="p-5 relative">
        <button 
          onClick={handleDismiss}
          className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <X className="h-4 w-4" />
        </button>
        <div className="flex items-start gap-4">
          <div className="h-10 w-10 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
            <Icon className="h-5 w-5 text-purple-500" />
          </div>
          <div className="flex-1 pr-6">
            <p className="text-sm text-purple-600 font-medium mb-1">A gentle reminder</p>
            <p className="font-heading text-forest text-lg leading-relaxed">
              "{affirmation}"
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Progress Acknowledgment Component (non-gamified)
export const GentleProgressCard = ({ milestone, userName }) => {
  const [visible, setVisible] = useState(true);
  const message = PROGRESS_MESSAGES[milestone] || "You're making beautiful progress.";
  const firstName = userName?.split(' ')[0] || 'Beautiful';

  if (!visible) return null;

  return (
    <Card className="card-base border border-emerald-200 bg-gradient-to-r from-emerald-50 to-teal-50/30 relative overflow-hidden">
      <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-bl from-emerald-100/50 to-transparent rounded-bl-full" />
      <CardContent className="p-5 relative">
        <button 
          onClick={() => setVisible(false)}
          className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <X className="h-4 w-4" />
        </button>
        <div className="flex items-start gap-4">
          <div className="h-10 w-10 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
            <Sparkles className="h-5 w-5 text-emerald-600" />
          </div>
          <div className="flex-1 pr-6">
            <p className="text-sm text-emerald-600 font-medium mb-1">{firstName}, look at you</p>
            <p className="font-heading text-forest text-base leading-relaxed">
              {message}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Morning/Evening Reflection Prompt
export const ReflectionPrompt = ({ timeOfDay = 'morning', focusArea = 'general' }) => {
  const [expanded, setExpanded] = useState(false);
  const [reflectionText, setReflectionText] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const morningPrompts = {
    career: "What is one thing you can do today that moves you closer to your purpose?",
    relationships: "Who needs your presence today? How can you show up for them?",
    faith: "What are you trusting God with today?",
    rest: "How will you protect your peace today?",
    visibility: "What fear can you face today, even just a little?",
    general: "What intention will guide your day?"
  };

  const eveningPrompts = {
    career: "What did you accomplish today that you're proud of?",
    relationships: "Who brought light to your day? Did you tell them?",
    faith: "Where did you see grace show up today?",
    rest: "Did you give yourself permission to rest today?",
    visibility: "How did you show up authentically today?",
    general: "What are you grateful for from today?"
  };

  const prompts = timeOfDay === 'morning' ? morningPrompts : eveningPrompts;
  const prompt = prompts[focusArea] || prompts.general;
  const Icon = timeOfDay === 'morning' ? Sun : Moon;

  const handleSaveToJournal = async () => {
    if (!reflectionText.trim()) {
      toast.error('Please write something before saving');
      return;
    }

    setSaving(true);
    try {
      const title = `${timeOfDay === 'morning' ? 'Morning' : 'Evening'} Reflection`;
      
      await axios.post(`${API_URL}/api/journal`, {
        title: title,
        content: `**Prompt:** ${prompt}\n\n**My Reflection:**\n${reflectionText}`,
        mood: 'reflective',
        tags: ['reflection', timeOfDay, focusArea],
        is_private: true
      });
      
      setSaved(true);
      toast.success('Reflection saved to your journal! 🌟', {
        description: 'Keep nurturing your inner wisdom.'
      });
      
      // Reset after a moment
      setTimeout(() => {
        setReflectionText('');
        setExpanded(false);
        setSaved(false);
      }, 2000);
      
    } catch (error) {
      console.error('Error saving reflection:', error);
      toast.error('Could not save reflection. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="card-base border-dashed border-2 border-amber-200 bg-amber-50/50">
      <CardContent className="p-4">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-full bg-amber-100 flex items-center justify-center">
            <Icon className="h-5 w-5 text-amber-600" />
          </div>
          <div className="flex-1">
            <p className="text-sm text-amber-700 font-medium">
              {timeOfDay === 'morning' ? 'Morning Reflection' : 'Evening Reflection'}
            </p>
            <p className="text-forest text-sm mt-1">{prompt}</p>
          </div>
          <Button 
            variant="ghost" 
            size="sm"
            onClick={() => setExpanded(!expanded)}
            className="text-amber-600 hover:text-amber-700 hover:bg-amber-100"
          >
            {expanded ? 'Close' : 'Reflect'}
          </Button>
        </div>
        {expanded && (
          <div className="mt-4 pt-4 border-t border-amber-200">
            <textarea 
              placeholder="Take a moment to write your thoughts..."
              className="w-full p-3 rounded-lg border border-amber-200 bg-white focus:ring-2 focus:ring-amber-200 focus:border-amber-300 outline-none resize-none text-sm"
              rows={3}
              value={reflectionText}
              onChange={(e) => setReflectionText(e.target.value)}
              disabled={saving || saved}
            />
            <div className="flex justify-end mt-2">
              <Button 
                size="sm" 
                className={`${saved ? 'bg-green-500 hover:bg-green-500' : 'bg-amber-500 hover:bg-amber-600'} text-white`}
                onClick={handleSaveToJournal}
                disabled={saving || saved || !reflectionText.trim()}
              >
                {saving ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving...</>
                ) : saved ? (
                  <><Check className="h-4 w-4 mr-2" /> Saved!</>
                ) : (
                  'Save to Journal'
                )}
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Export all affirmations and messages for use elsewhere
export { AFFIRMATIONS, PROGRESS_MESSAGES };
